import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { newsList, newsCategories } from "@/mocks/news";
import type { NewsItem } from "@/types";
import { formatDate } from "@/utils/content";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";

const PAGE_SIZE = 10;

function NewsRow({ item }: { item: NewsItem }) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex cursor-pointer flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row md:p-5"
    >
      <div className="w-full shrink-0 overflow-hidden rounded-md sm:w-[220px]" style={{ height: 150 }}>
        <img
          src={item.image}
          alt={`${item.title} 配图`}
          title={`${item.title} - 风间由美新闻资讯`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
          <span className="rounded-full bg-primary-100 px-2.5 py-0.5 font-medium text-primary-700">
            {item.category}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {formatDate(item.date)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {item.author}
          </span>
        </div>
        <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {item.summary}
        </p>
        <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-medium text-primary-600">
          阅读全文
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

export default function NewsPage() {
  useSeo({
    title: "新闻资讯 - 风间由美官方网站",
    description:
      "风间由美新闻资讯栏目，汇总官方公告、影视动态、音乐发行、活动现场与公益行动等最新消息，支持按时间排序与关键词筛选。",
    keywords: "风间由美新闻,风间由美资讯,风间由美最新动态,官方公告,影视动态",
  });

  const [searchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(urlQuery);
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    setPage(1);
  }, [keyword, sort, category]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = newsList.filter((item) => {
      const matchKw =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((t) => t.toLowerCase().includes(kw));
      const matchCat = category === "全部" || item.category === category;
      return matchKw && matchCat;
    });
    return list.sort((a, b) => (sort === "desc" ? (a.date < b.date ? 1 : -1) : a.date < b.date ? -1 : 1));
  }, [keyword, sort, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setKeyword("");
    setCategory("全部");
    setSort("desc");
    setPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "新闻资讯" }]} />

      <header className="mt-6 border-b border-background-200 pb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
          新闻资讯
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          这里汇集风间由美的官方公告、影视动态、音乐发行、活动现场与公益行动等消息。你可以按发布时间排序，也可以用关键词快速筛选标题与摘要。
        </p>
      </header>

      <div className="mt-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {newsCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                category === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center rounded-full border border-background-300 bg-background-50 pl-3 focus-within:border-primary-400">
            <span className="flex h-9 w-6 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="筛选标题关键词"
              aria-label="筛选新闻标题关键词"
              className="w-full bg-transparent py-2 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none sm:w-52"
            />
          </div>
          <div className="flex items-center rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort("desc")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                sort === "desc" ? "bg-background-50 text-primary-600" : "text-foreground-600"
              }`}
            >
              最新优先
            </button>
            <button
              type="button"
              onClick={() => setSort("asc")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                sort === "asc" ? "bg-background-50 text-primary-600" : "text-foreground-600"
              }`}
            >
              最早优先
            </button>
          </div>
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-500">
        共筛选出 <strong className="text-primary-600">{filtered.length}</strong> 条资讯
        {keyword.trim() ? `，关键词「${keyword.trim()}」` : ""}
      </p>

      {pageItems.length > 0 ? (
        <div className="mt-6 flex flex-col gap-4">
          {pageItems.map((item) => (
            <NewsRow key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-primary-500">
            <i className="ri-search-eye-line text-2xl" />
          </span>
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
            没有匹配的资讯
          </h3>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            试试更换关键词，或重置筛选条件查看全部资讯。
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      )}

      {totalPages > 1 && pageItems.length > 0 ? (
        <nav
          aria-label="分页导航"
          className="mt-10 flex flex-wrap items-center justify-center gap-2"
        >
          <button
            type="button"
            disabled={safePage === 1}
            onClick={() => setPage(safePage - 1)}
            className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors enabled:cursor-pointer enabled:hover:border-primary-400 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-s-line" />
            上一页
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                p === safePage
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={safePage === totalPages}
            onClick={() => setPage(safePage + 1)}
            className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors enabled:cursor-pointer enabled:hover:border-primary-400 enabled:hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一页
            <i className="ri-arrow-right-s-line" />
          </button>
        </nav>
      ) : null}
    </div>
  );
}